/**
 * Who may download a patient's full record as PDF: the patient report and the letters
 * and documents bundle (KIN-2201, KIN-2207). The backend accepts exactly these on both
 * endpoints and answers anyone else 403, so the buttons are shown to the same people.
 * "Export full patient record" is a permission an admin grants per staff member.
 */
export const PATIENT_RECORD_EXPORT_ROLES = [
  "ADMIN",
  "ROLE_ADMIN",
  "ROLE_EXPORT_PATIENT_RECORD",
];

/** The file name the server put on the attachment, or the fallback. */
export const pdfFilename = (headers, fallback) => {
  const contentDisposition = headers?.["content-disposition"] ?? "";
  const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/);

  return filenameMatch?.[1] ?? headers?.["file-name"] ?? fallback;
};

// FileReader rather than Blob.text(): the same code then runs in jsdom, which has no
// Blob.text, and in every browser the app supports.
const readBlobText = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/**
 * A failed blob request carries its error body as a Blob, so the server's sentence is
 * unreadable to `getServerErrorMessage` until it is turned back into JSON. A body that
 * is not JSON is kept as plain text under `message`, and a body that cannot be read at
 * all leaves the error as it was.
 */
export const withReadableErrorBody = async (error) => {
  const body = error?.response?.data;

  if (typeof Blob === "undefined" || !(body instanceof Blob)) return error;

  try {
    const text = await readBlobText(body);
    let data;

    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }

    error.response.data = data;
  } catch {
    // Unreadable body: the caller's fallback message stands.
  }

  return error;
};
